/**
 * Underage Message Component
 *
 * Shows age-appropriate messaging for users under 13:
 * - Ages 8-12: Guidance to use Family PIN login
 * - Under 8: Come back when older
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'flowbite-react';
import EnhancedButton from './ui/EnhancedButton';

interface UnderageMessageProps {
  age?: number;
  onBack: () => void;
}

const UnderageMessage: React.FC<UnderageMessageProps> = ({ age, onBack }) => {
  const navigate = useNavigate();
  const isEligibleForPIN = age && age >= 8 && age <= 12;
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-lg shadow-2xl border-none">
        <div className="p-8 text-center">
          {/* Icon */}
          <div className="text-6xl mb-6">{isEligibleForPIN ? '🔐' : '🌱'}</div>

          {/* Header */}
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            {isEligibleForPIN ? 'Use Family PIN Login!' : 'Thanks for Your Interest!'}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {isEligibleForPIN
              ? 'You can access HoneyDo using your Family PIN'
              : 'HoneyDo requires users to be at least 8 years old'}
          </p>

          {isEligibleForPIN ? (
            /* Ages 8-12: Family PIN Instructions */
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-gray-800 mb-4 text-center">How to Access HoneyDo 🔐</h3>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">👨‍👩‍👧‍👦</span>
                  <div>
                    <div className="font-medium">Parent Must Have Account</div>
                    <div className="text-gray-600">Your parent or guardian needs to create a HoneyDo account first</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">🔐</span>
                  <div>
                    <div className="font-medium">Family PIN Setup</div>
                    <div className="text-gray-600">Ask your parent to create a Family PIN in their account settings</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <div className="font-medium">Log In with PIN</div>
                    <div className="text-gray-600">Use the Family PIN login button instead of signing up</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Under 8: Too Young Message */
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-gray-800 mb-4 text-center">Age Requirements 🌱</h3>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📅</span>
                  <div>
                    <div className="font-medium">Minimum Age</div>
                    <div className="text-gray-600">HoneyDo requires users to be at least 8 years old for Family PIN access</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">⏰</span>
                  <div>
                    <div className="font-medium">Come Back Later</div>
                    <div className="text-gray-600">Check back when you're 8 years old to join your family's HoneyDo</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-2xl">👨‍👩‍👧‍👦</span>
                  <div>
                    <div className="font-medium">Family Members Can Use It</div>
                    <div className="text-gray-600">Ask your parents to try HoneyDo to manage family tasks</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-4">
            {isEligibleForPIN ? (
              <>
                <EnhancedButton
                  variant="premium"
                  onClick={() => navigate('/family-login')}
                  className="w-full"
                  glow
                >
                  Go to Family PIN Login
                </EnhancedButton>
                <EnhancedButton
                  variant="secondary"
                  onClick={onBack}
                  className="w-full"
                >
                  Back to Age Verification
                </EnhancedButton>
              </>
            ) : (
              <EnhancedButton
                variant="primary"
                onClick={onBack}
                className="w-full"
                glow
              >
                Back to Age Verification
              </EnhancedButton>
            )}

            <div className="text-sm text-gray-500">
              Have questions? Contact us at{' '}
              <a href="mailto:coppa@honeydolist.vip" className="text-purple-600 hover:text-purple-700 font-medium">
                coppa@honeydolist.vip
              </a>
            </div>
          </div>
        </div>
      </Card>

      {/* Footer */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-center">
        <p className="text-sm text-gray-500">
          © 2025 HoneyDo • Family Task Management Platform
        </p>
      </div>
    </div>
  );
};

export default UnderageMessage;